window.App = function App() {
  const [activeTab, setActiveTab] = React.useState('home');
  const [quoteModalOpen, setQuoteModalOpen] = React.useState(false);
  const [adminUser, setAdminUser] = React.useState(null);
  const [checkingAuth, setCheckingAuth] = React.useState(true);
  const [categoriesList, setCategoriesList] = React.useState(['All', 'Railways', 'Industrial Automation', 'Aerospace', 'Automotive', 'Custom CAD & Fixtures']);
  const [selectedProductCategory, setSelectedProductCategory] = React.useState('All');
  const [selectedProductSlug, setSelectedProductSlug] = React.useState('railway-brake-valve-test-rig');
  const [selectedIndustrySlug, setSelectedIndustrySlug] = React.useState('railway');
  const [selectedSolutionSlug, setSelectedSolutionSlug] = React.useState('railway-testing');
  const [selectedCategoryData, setSelectedCategoryData] = React.useState(null);

  const parseUrl = () => {
    // Get pathname segments
    const rawPath = window.location.pathname;
    const segments = rawPath.split('/').filter(Boolean);
    const knownPages = ['home', 'industries', 'products', 'product-detail', 'category', 'about', 'blogs', 'careers', 'india', 'downloads', 'contact', 'support', 'admin'];
    
    let initialTab = 'home';
    
    // Strictly route only root-level page segments
    if (segments.length > 0) {
      const firstSegment = segments[0];
      if (knownPages.includes(firstSegment)) {
        initialTab = firstSegment;
      }
    }

    // 2. Hash check (admin fallback)
    const hash = window.location.hash.replace('#', '');
    if (hash === 'admin') {
      initialTab = 'admin';
    }
    
    // 3. Query params check
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    const slugParam = params.get('slug');
    const catParam = params.get('cat');
    
    if (tabParam) initialTab = tabParam;
    
    setActiveTab(initialTab);
    if (slugParam) {
      setSelectedProductSlug(slugParam);
      setSelectedIndustrySlug(slugParam);
      setSelectedSolutionSlug(slugParam);
    }
    if (catParam) setSelectedProductCategory(catParam);
  };

  // Check URL params and hash on load
  React.useEffect(() => {
    parseUrl();
    
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'admin') {
        setActiveTab('admin');
      }
    };
    
    const handlePopState = (event) => {
      const state = event.state;
      if (state && state.activeTab) {
        setActiveTab(state.activeTab);
        if (state.selectedProductSlug) setSelectedProductSlug(state.selectedProductSlug);
        if (state.selectedProductCategory) setSelectedProductCategory(state.selectedProductCategory);
      } else {
        parseUrl();
      }
    };
    
    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('popstate', handlePopState);
    checkSession();
    fetchCategories();

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Synchronize state changes to clean URLs
  React.useEffect(() => {
    if (checkingAuth) return; // Wait for initial load check
    
    // Sync URL strictly relative to root path since app is hosted at domain root
    let targetPath = activeTab === 'home' ? '/' : `/${activeTab}`;
    if (activeTab === 'admin') {
      targetPath = '/#admin';
    }
    
    const params = new URLSearchParams();
    if (activeTab === 'product-detail' && selectedProductSlug) {
      params.set('slug', selectedProductSlug);
    }
    if (activeTab === 'solutions' && selectedSolutionSlug) {
      params.set('slug', selectedSolutionSlug);
    }
    if (activeTab === 'industries' && selectedIndustrySlug) {
      params.set('slug', selectedIndustrySlug);
    }
    if (activeTab === 'products' && selectedProductCategory && selectedProductCategory !== 'All') {
      params.set('cat', selectedProductCategory);
    }
    
    const queryString = params.toString();
    const fullUrl = targetPath + (queryString ? `?${queryString}` : '');
    
    // Only push state if url changed to avoid loops
    const currentFull = window.location.pathname + window.location.search + window.location.hash;
    if (currentFull !== fullUrl) {
      window.history.pushState({ activeTab, selectedProductSlug, selectedProductCategory }, '', fullUrl);
    }
  }, [activeTab, selectedProductSlug, selectedProductCategory, selectedSolutionSlug, selectedIndustrySlug, checkingAuth]);

  const fetchCategories = async () => {
    try {
      const res = await window.apiClient.get('categories.php');
      if (res.success && res.data && res.data.length > 0) {
        const catNames = res.data.map(c => c.name);
        setCategoriesList(['All', ...catNames]);
      }
    } catch (e) {
      console.warn('App categories fetch fallback:', e);
    }
  };

  const checkSession = async () => {
    try {
      const res = await window.apiClient.get('auth.php?action=check');
      if (res.success && res.authenticated) {
        setAdminUser(res.user);
      }
    } catch (e) {
      console.warn('Session check warning:', e);
    } finally {
      setCheckingAuth(false);
    }
  };

  // Reset scroll position when switching tabs
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab, selectedProductSlug, selectedIndustrySlug, selectedSolutionSlug]);

  // Render Admin View
  if (activeTab === 'admin') {
    if (adminUser) {
      return (
        <window.AdminLayout 
          user={adminUser} 
          onLogout={async () => {
            try { await window.apiClient.get('auth.php?action=logout'); } catch (e) {}
            setAdminUser(null);
            setActiveTab('home');
            window.location.hash = '';
          }} 
        />
      );
    } else {
      return (
        <window.AdminLogin 
          onLoginSuccess={(user) => {
            setAdminUser(user);
          }} 
        />
      );
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900 font-sans antialiased selection:bg-secondary selection:text-white">
      
      {/* Sticky Header with 9 Top-Level Navigation Nodes */}
      <window.Header 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        openQuoteModal={() => setQuoteModalOpen(true)} 
        categoriesList={categoriesList}
        selectedProductCategory={selectedProductCategory}
        setSelectedProductCategory={setSelectedProductCategory}
        setSelectedIndustrySlug={setSelectedIndustrySlug}
      />

      {/* Main Content View Switcher */}
      <main className="flex-grow">
        {activeTab === 'home' && (
          <window.HomePage 
            setActiveTab={setActiveTab} 
            openQuoteModal={() => setQuoteModalOpen(true)} 
            setSelectedIndustrySlug={setSelectedIndustrySlug}
            setSelectedProductSlug={setSelectedProductSlug}
            setSelectedProductCategory={setSelectedProductCategory}
          />
        )}

        {activeTab === 'industries' && (
          <window.IndustriesPage 
            selectedIndustrySlug={selectedIndustrySlug}
            setActiveTab={setActiveTab}
            openQuoteModal={() => setQuoteModalOpen(true)}
            setSelectedProductSlug={setSelectedProductSlug}
            setSelectedProductCategory={setSelectedProductCategory}
            setSelectedCategoryData={setSelectedCategoryData}
          />
        )}

        {activeTab === 'category' && (
          <window.CategoryPage 
            categoryData={selectedCategoryData}
            setActiveTab={setActiveTab}
            setSelectedProductSlug={setSelectedProductSlug}
            openQuoteModal={() => setQuoteModalOpen(true)}
          />
        )}

        {activeTab === 'products' && (
          <window.ProductsPage 
            openQuoteModal={() => setQuoteModalOpen(true)} 
            selectedCategory={selectedProductCategory}
            setSelectedCategory={setSelectedProductCategory}
            categoriesList={categoriesList}
            setActiveTab={setActiveTab}
            setSelectedProductSlug={setSelectedProductSlug}
          />
        )}

        {activeTab === 'product-detail' && (
          <window.ProductDetailPage 
            productSlug={selectedProductSlug}
            setActiveTab={setActiveTab}
            openQuoteModal={() => setQuoteModalOpen(true)}
            setSelectedProductSlug={setSelectedProductSlug}
          />
        )}


        {activeTab === 'about' && (
          <window.AboutPage 
            setActiveTab={setActiveTab} 
            openQuoteModal={() => setQuoteModalOpen(true)} 
          />
        )}

        {activeTab === 'blogs' && (
          <window.BlogsPage />
        )}


        {activeTab === 'careers' && (
          <window.CareersPage setActiveTab={setActiveTab} />
        )}

        {activeTab === 'contact' && (
          <window.ContactPage 
            openQuoteModal={() => setQuoteModalOpen(true)} 
          />
        )}

        {activeTab === 'support' && (
          <window.SupportPage />
        )}

        {activeTab === 'login' && (
          <window.LoginPage setActiveTab={setActiveTab} />
        )}

        {activeTab === 'india' && (
          <window.IndiaPage setActiveTab={setActiveTab} />
        )}
      </main>

      {/* Global Footer */}
      <window.Footer 
        setActiveTab={setActiveTab} 
        openQuoteModal={() => setQuoteModalOpen(true)} 
        setSelectedIndustrySlug={setSelectedIndustrySlug}
        setSelectedProductCategory={setSelectedProductCategory}
      />

      {/* Engineering RFQ / Consultation Modal */}
      <window.QuoteModal 
        isOpen={quoteModalOpen} 
        onClose={() => setQuoteModalOpen(false)} 
      />

    </div>
  );
};
